import React from 'react';

import { HandCraftingQueueEntry } from '../HandCrafting';

interface HandCraftingStatusProps {
  queue: readonly HandCraftingQueueEntry[];
}

const formatQueueSummary = (
  queue: readonly HandCraftingQueueEntry[]
): number => {
  if (queue.length === 0) {
    return 0;
  }

  const [active, ...rest] = queue;
  if (!active) {
    return 0;
  }
  const remainingAfterActive = Math.max(0, active.remainingCount - 1);
  const restTotal = rest.reduce((sum, entry) => sum + entry.remainingCount, 0);
  return remainingAfterActive + restTotal;
};

export const HandCraftingStatus: React.FC<HandCraftingStatusProps> = ({
  queue,
}) => {
  if (queue.length === 0) {
    return (
      <div className='hand-crafting-status'>
        <h4>Hand Crafting</h4>
        <p className='hand-crafting-status__idle'>
          No hand crafting in progress
        </p>
      </div>
    );
  }

  const active = queue[0];
  if (!active) {
    return null;
  }
  const rawProgress =
    active.ticksPerCraft > 0 ? active.progressTicks / active.ticksPerCraft : 0;
  const clampedProgress = Math.min(Math.max(rawProgress, 0), 1);
  const progressPercent = clampedProgress * 100;
  const progressLabel =
    progressPercent >= 99.95
      ? '100%'
      : progressPercent >= 10
        ? `${Math.round(progressPercent)}%`
        : `${progressPercent.toFixed(1)}%`;
  const currentIndex = Math.min(
    active.totalCount,
    Math.max(1, active.totalCount - active.remainingCount + 1)
  );
  const queueRemaining = formatQueueSummary(queue);

  return (
    <div className='hand-crafting-status'>
      <h4>Hand Crafting</h4>
      <div className='hand-crafting-status__current'>
        <div className='hand-crafting-status__title'>{active.recipeName}</div>
        <div className='hand-crafting-status__progress-bar'>
          <div
            className='hand-crafting-status__progress-fill'
            style={{ width: `${progressPercent}%` }}
          />
        </div>
        <div className='hand-crafting-status__details'>
          <span>
            {currentIndex}/{active.totalCount}
          </span>
          <span>{progressLabel}</span>
        </div>
      </div>
      {queueRemaining > 0 && (
        <div className='hand-crafting-status__queue'>
          Queue: {queueRemaining} craft{queueRemaining === 1 ? '' : 's'}{' '}
          remaining
        </div>
      )}
    </div>
  );
};
